import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X, ArrowUpDown, CheckCircle2, Clock } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import Combobox from "@/components/ui/common/combobox";
import { useCriteriaValidationQuery } from "@/services/facilityApis";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { ErrorType } from "@/types";

interface SwapBookerProps {
  isOpen: boolean;
  onClose: () => void;
  currentBooker: {
    id: number | string;
    name: string;
  };
  availableBookers: Array<{ id: number | string; name: string }>;
  onSwap: (newBookerId: number | string) => Promise<void>;
  facilityId: number;
}

type FormValues = {
  replacementBookerId: string | number;
};

const SwapBooker = ({
  isOpen,
  onClose,
  currentBooker,
  availableBookers,
  onSwap,
  facilityId,
}: SwapBookerProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedMemberId, setSelectedMemberId] = React.useState<number | string>("");
  
  const orgId = useSelector(
    (state: RootState) => state.auth.userInfo?.user?.org_id
  ) || 0;

  // Criteria validation query
  const {
    data: criteriaValidationData,
    isLoading: isCriteriaValidating,
    error: criteriaValidationError,
    isError: isCriteriaValidationError,
  } = useCriteriaValidationQuery({
    org_id: orgId,
    member_id: Number(selectedMemberId),
    facility_id: facilityId
  }, {
    skip: !selectedMemberId || !facilityId || !orgId || selectedMemberId === ""
  });

  const typedCriteriaValidationError = criteriaValidationError as ErrorType;
  const criteriaValidationMessage = criteriaValidationError ? 
    `${typedCriteriaValidationError.data?.detail || typedCriteriaValidationError.data?.message}` : 
    "Member does not meet facility criteria";

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      replacementBookerId: "",
    },
  });

  const watchedReplacementBookerId = watch("replacementBookerId");

  // Update selectedMemberId when form value changes
  React.useEffect(() => {
    if (watchedReplacementBookerId && watchedReplacementBookerId !== "") {
      setSelectedMemberId(watchedReplacementBookerId);
    } else {
      setSelectedMemberId("");
    }
  }, [watchedReplacementBookerId]);

  // Reset form and state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedMemberId("");
      setValue("replacementBookerId", "");
    }
  }, [isOpen, setValue]);

  // Transform availableBookers to the format expected by Combobox
  const bookerOptions = React.useMemo(() => {
    return availableBookers.map((booker) => ({
      label: booker.name,
      value: booker.id.toString(),
    }));
  }, [availableBookers]);

  const onSubmit = async (data: FormValues) => {
    try {
      // Check if criteria validation passed
      if (!criteriaValidationData && selectedMemberId) {
        toast({
          variant: "destructive",
          title: "Validation Required",
          description: "Please wait for member criteria validation to complete.",
        });
        return;
      }

      // Check if validation failed
      if (criteriaValidationError) {
        toast({
          variant: "destructive",
          title: "Validation Failed",
          description: criteriaValidationMessage,
        });
        return;
      }

      setIsLoading(true);
      await onSwap(data.replacementBookerId);
      toast({
        variant:"success",
        title: "Success",
        description: "Booker has been swapped successfully",
      });
      onClose();
    } catch (error) {
      console.error("Error swapping booker:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to swap booker. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Swap Booker</SheetTitle>
            {/* <SheetClose asChild>
              <Button size="icon" variant="ghost">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose> */}
          </div>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Current Booker</label>
              <div className="h-10 mt-1 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 capitalize">
                {currentBooker?.name || "N/A"}
              </div>
            </div>

            <div className="flex items-center justify-center">
              <ArrowUpDown className="text-gray-400" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Replacement Booker</label>
              <Controller
                name="replacementBookerId"
                control={control}
                rules={{ required: "Please select a replacement booker" }}
                render={({ field: { value, onChange } }) => (
                  <Combobox
                    list={bookerOptions}
                    defaultValue={bookerOptions.find(option => option.value === value?.toString())?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = bookerOptions.find(option => option.label === selectedLabel);
                      onChange(selectedOption ? selectedOption.value : "");
                    }}
                    label="Replacement Booker"
                    showLabel={false}
                  />
                )}
              />
              {errors.replacementBookerId && (
                <p className="text-sm text-red-500">
                  {errors.replacementBookerId.message}
                </p>
              )}
            </div>

            {/* Criteria Validation Status */}
            {selectedMemberId && facilityId && (
              <div className="mt-4 space-y-3">
                {/* Loading State */}
                {isCriteriaValidating && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                      <Clock className="h-4 w-4 animate-spin" />
                      Validating member criteria...
                    </div>
                  </div>
                )}

                {/* Success State */}
                {criteriaValidationData && !criteriaValidationError && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                      <CheckCircle2 className="h-4 w-4" />
                      Member meets facility criteria
                    </div>
                  </div>
                )}

                {/* Error State */}
                {criteriaValidationError && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
                      <i className="fa fa-exclamation-triangle h-4 w-4"></i>
                      {criteriaValidationMessage}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              className="bg-green-500 hover:bg-green-600 text-white"
              disabled={isLoading || (selectedMemberId !== "" && !!criteriaValidationError) || (selectedMemberId !== "" && isCriteriaValidating)}
            >
              {isLoading ? "Processing..." : "Swap"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default SwapBooker;